import React, { useState, useEffect, useMemo } from 'react';
import { UserProfile, FinancialConfig, Quote } from '../types';
import { 
  ClipboardList, 
  DollarSign, 
  Clock, 
  TrendingUp, 
  CheckCircle2, 
  XCircle, 
  Clock3, 
  UserCircle, 
  Globe, 
  Lightbulb, 
  FileText, 
  LifeBuoy, 
  ArrowLeft,
  ChevronRight,
  Camera,
  Palette,
  Scissors,
  Code,
  Utensils,
  GraduationCap,
  Hammer,
  ShoppingBag,
  Briefcase,
  Calculator,
  Package,
  Plus,
  ChefHat,
  MessageCircle,
  Send,
  RefreshCcw,
  Zap
} from 'lucide-react';
import { formatMinutes, translateQuoteStatus } from '../utils/formatUtils';
import { calculatePriceHealth } from '../utils/gamificationUtils';
import ProfitThermometer from './ProfitThermometer';
import HelpCenterModal from './HelpCenterModal';
import { SetupProgressModal } from './SetupProgressModal';
import PaywallModal from './PaywallModal';
import FontSizeSelector from './FontSizeSelector';
import TaxReformNoticeBanner from './TaxReformNoticeBanner';
import { motion, AnimatePresence } from 'motion/react';
import { CATEGORIES } from '../constants/categories';

const PDFExporter = React.lazy(() => import('./PDFExporter'));

const Tooltip = ({ children, text }: { children: React.ReactNode, text: string }) => {
  const [isVisible, setIsVisible] = useState(false);
  return (
    <div 
      className="relative flex items-center"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onClick={() => setIsVisible(!isVisible)}
    >
      {children}
      <AnimatePresence>
        {isVisible && (
          <motion.div 
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 p-3 bg-zinc-900 text-white text-[10px] rounded-2xl shadow-2xl z-50 text-center leading-relaxed"
          >
            {text}
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-zinc-900" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface Props {
  user: UserProfile;
  config: FinancialConfig;
  quotes: Quote[];
  materials: any[];
  onUpdateQuoteStatus: (id: string, status: Quote['status']) => void;
  onEditProfile: (step?: any) => void;
  onOpenComparison: () => void;
  onOpenHistory: () => void;
  onOpenMaterials: () => void;
  onBack: () => void;
  onReuseQuote?: (quote: Quote) => void;
}

const getNicheIcon = (niche: string) => {
  const n = niche.toLowerCase();
  if (n.includes('foto') || n.includes('vídeo') || n.includes('audio')) return Camera;
  if (n.includes('design') || n.includes('arte') || n.includes('ilustra')) return Palette;
  if (n.includes('artesanato') || n.includes('costura')) return Scissors;
  if (n.includes('program') || n.includes('dev') || n.includes('tech') || niche.includes('ti ')) return Code;
  if (n.includes('comida') || n.includes('gastro') || n.includes('doce') || n.includes('confeitaria')) return Utensils;
  if (n.includes('beleza') || n.includes('estética') || n.includes('moda') || n.includes('barbeiro') || n.includes('tatuagem')) return Scissors;
  if (n.includes('aula') || n.includes('educ') || n.includes('ensino') || n.includes('professor')) return GraduationCap;
  if (n.includes('obra') || n.includes('reforma') || n.includes('marcenaria') || n.includes('eletricista')) return Hammer;
  if (n.includes('loja') || n.includes('venda') || n.includes('comércio') || n.includes('revenda')) return ShoppingBag;
  return Briefcase;
};

// Funções auxiliares puras movidas para fora do componente
const calculateCompletion = (user: UserProfile, config: FinancialConfig, materials: any[]) => {
  let completedSteps = 0;
  
  // 1. Identidade (10%) - Nome e E-mail
  if (user.ownerName && user.email) completedSteps++;
  // 2. Nicho de Atuação (10%) - Nicho e Profissão
  if (user.niche && user.profession) completedSteps++;
  // 3. Marca e Identidade Visual (10%) - Nome da Marca e Logo
  if (user.businessName && (user.logoUrl || user.logoBase64)) completedSteps++;
  // 4. Localização (10%) - Cidade, Estado e CEP
  if (user.city && user.state && user.zipCode) completedSteps++;
  // 5. Configuração Fiscal (10%) - Regime Tributário e Dados de Recebimento
  if (user.taxRegime && (user.pixKey || user.pixKeyType)) completedSteps++;
  // 6. Meta de Pró-labore (10%)
  if (config.proLaboreGoal > 0) completedSteps++;
  // 7. Custos Fixos (10%)
  if (config.monthlyFixedCost > 0) completedSteps++;
  // 8. Jornada de Trabalho (10%)
  if (config.weeklyWorkHours > 0) completedSteps++;
  // 9. Centro de Custos / Inventário (10%) - Ter pelo menos um item cadastrado
  if (materials && materials.length > 0) completedSteps++;
  // 10. Termos e LGPD (10%) - Aceite dos termos e privacidade
  if (user.lgpdConsent?.termsAccepted && user.lgpdConsent?.privacyPolicyAccepted) completedSteps++;
  
  return completedSteps * 10;
};

const getPendingTask = (
  user: UserProfile,
  config: FinancialConfig,
  materials: any[],
  onEditProfile: (step?: any) => void,
  onOpenMaterials: () => void
) => {
  // Ordem de prioridade baseada na jornada
  if (!user.ownerName || !user.email) return { label: 'Complete sua Identidade', action: () => onEditProfile('personal') };
  if (!user.niche || !user.profession) return { label: 'Defina seu Nicho e Profissão', action: () => onEditProfile('business') };
  if (!user.businessName || (!user.logoUrl && !user.logoBase64)) return { label: 'Configure sua Marca e Logo', action: () => onEditProfile('business') };
  if (!user.city || !user.state || !user.zipCode) return { label: 'Informe sua Localização', action: () => onEditProfile('location') };
  if (!user.taxRegime || (!user.pixKey && !user.pixKeyType)) return { label: 'Configure seus Dados Fiscais/PIX', action: () => onEditProfile('business') };
  if (config.proLaboreGoal <= 0) return { label: 'Defina seu Salário Desejado', action: () => onEditProfile('financial') };
  if (config.monthlyFixedCost <= 0) return { label: 'Cadastre seus Custos Fixos', action: () => onEditProfile('financial') };
  if (config.weeklyWorkHours <= 0) return { label: 'Defina sua Jornada de Trabalho', action: () => onEditProfile('financial') };
  if ((materials?.length || 0) === 0) return { label: 'Cadastre seu Primeiro Custo', action: onOpenMaterials };
  if (!user.lgpdConsent?.termsAccepted) return { label: 'Aceite os Termos Legais', action: () => onEditProfile('lgpd') };
  
  return { label: 'Tudo pronto para orçar! 🚀', action: () => {} };
};

export default function Dashboard({ user, config, quotes, materials, onUpdateQuoteStatus, onEditProfile, onOpenComparison, onOpenHistory, onOpenMaterials, onBack, onReuseQuote }: Props) {
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const [toastMessage, setToastMessage] = useState<{title: string, type: 'success' | 'error' | 'info'} | null>(null);

  const showToast = (title: string, type: 'success' | 'error' | 'info') => {
    setToastMessage({ title, type });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: Quote['status']) => {
    switch (status) {
      case 'approved': return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
      case 'sent': return <Clock3 className="h-4 w-4 text-amber-600" />;
      case 'rejected': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'draft': return <FileText className="h-4 w-4 text-zinc-400" />;
      default: return null;
    }
  };

  const getStatusColor = (status: Quote['status']) => {
    switch (status) {
      case 'approved': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'sent': return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'rejected': return 'bg-red-50 text-red-700 border-red-200';
      case 'draft': return 'bg-zinc-100 text-zinc-600 border-zinc-200';
      default: return 'bg-zinc-100 text-zinc-600 border-zinc-200';
    }
  };

  const calculateDaysSince = (dateString: string) => {
    const created = new Date(dateString);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - created.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const computeDriftAlerts = (quote: Quote): Array<{ name: string; snapshotPrice: number; currentPrice: number; driftPct: number }> => {
    if (!quote.products?.length || !materials.length) return [];
    const alerts: Array<{ name: string; snapshotPrice: number; currentPrice: number; driftPct: number }> = [];

    quote.products.forEach((p: any) => {
      if (typeof p.snapshotPrice !== 'number') return;

      const current = p.materialId
        ? materials.find(m => m.id === p.materialId)
        : materials.find(m => {
            const mn = (m.name || '').toLowerCase();
            const pn = (p.name || '').toLowerCase();
            return mn.includes(pn) || pn.includes(mn);
          });

      if (!current) return;

      const driftPct = ((current.pricePaid - p.snapshotPrice) / p.snapshotPrice) * 100;
      if (Math.abs(driftPct) >= 5) {
        alerts.push({
          name: p.name,
          snapshotPrice: p.snapshotPrice,
          currentPrice: current.pricePaid,
          driftPct,
        });
      }
    });

    return alerts;
  };

  const NicheIcon = getNicheIcon(user?.niche || 'Geral');
  const [filterStatus, setFilterStatus] = useState<Quote['status'] | 'Todos'>('Todos');
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const [isSetupModalOpen, setIsSetupModalOpen] = useState(() => {
    const saved = localStorage.getItem('rmax_setup_modal_open');
    const seen = localStorage.getItem('rmax_setup_modal_seen');
    const comp = calculateCompletion(user, config, materials);
    
    // Abre automaticamente apenas se: nunca foi aberto antes E o setup está incompleto
    if (saved === null && seen === null && comp < 100) return true;
    return saved === 'true';
  });

  useEffect(() => {
    localStorage.setItem('rmax_setup_modal_open', isSetupModalOpen.toString());
  }, [isSetupModalOpen]);

  const handleCloseSetupModal = () => {
    setIsSetupModalOpen(false);
    localStorage.setItem('rmax_setup_modal_seen', 'true');
  };
  
  // Cálculos pesados memorizados
  const completion = useMemo(() => calculateCompletion(user, config, materials), [user, config, materials]);
  
  const pendingTask = useMemo(() => getPendingTask(user, config, materials, onEditProfile, onOpenMaterials), [user, config, materials, onEditProfile, onOpenMaterials]);

  const priceHealth = useMemo(() => calculatePriceHealth(user, materials), [user, materials]);

  const metrics = useMemo(() => {
    const pendingQuotes = (quotes || []).filter((q) => q.status === 'sent');
    const closedQuotes = (quotes || []).filter((q) => q.status === 'approved');
    
    const totalMoneyOnTable = pendingQuotes.reduce((acc, q) => acc + Number(q.finalPrice || 0), 0);
    const totalRevenue = closedQuotes.reduce((acc, q) => acc + Number(q.finalPrice || 0), 0);
    const totalMaterialsCost = closedQuotes.reduce((acc, q) => acc + Number(q.materialCost || 0), 0);
    const totalNetProfit = totalRevenue - totalMaterialsCost;

    return {
      totalMoneyOnTable,
      totalNetProfit
    };
  }, [quotes]);

  const formatCurrency = (value: number) => {
    const formatted = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    return formatted.replace(/^R\$\s?R\$/, 'R$');
  };

  const handleFollowUp = async (quote: Quote) => {
    const userName = (user.ownerName || '').split(' ')[0];
    const clientName = (quote.clientName || 'Cliente').split(' ')[0];
    const message = `Olá ${clientName}${userName ? `, aqui é o ${userName}` : ''}. Passando para saber se conseguiu avaliar o orçamento que te enviei. Alguma dúvida?`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Cobrar Retorno do Orçamento',
          text: message
        });
        return;
      } catch (e) {
        console.warn('Web Share API falhou, usando fallback wa.me:', e);
      }
    }

    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="h-dvh flex flex-col bg-zinc-50 font-sans overflow-hidden relative">
      {/* Header - Fixed & Compact */}
      <div className="bg-brand text-white px-4 py-4 rounded-b-4xl shadow-lg z-20 shrink-0">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-3 flex-1">
            <button 
              onClick={onBack}
              className="bg-white/20 p-2 rounded-xl backdrop-blur-sm hover:bg-white/30 transition-colors"
            >
              <ArrowLeft className="h-6 w-6 text-white" />
            </button>
            <div className="flex-1">
              <button 
                onClick={() => {
                  console.log('R-MAX: Abrindo Modal de Setup');
                  setIsSetupModalOpen(true);
                }}
                className="w-full text-left group cursor-pointer"
              >
                <div className="flex justify-between items-end mb-1">
                  <Tooltip text="Passo a passo do seu negócio: Complete as 10 etapas (Perfil, Localização, Empresa, Privacidade, Metas, Custos Fixos, Horas e Primeiro Material) para atingir 100%.">
                    <p className="text-brand-dark/60 text-[10px] font-black uppercase tracking-widest flex items-center gap-2 bg-white/40 px-2 py-0.5 rounded-md backdrop-blur-md">
                      Passo a Passo
                      <ChevronRight className="h-3 w-3 opacity-50 group-hover:opacity-100 transition-opacity" />
                    </p>
                  </Tooltip>
                  <span className="text-[10px] font-black text-white bg-black/10 px-2 py-0.5 rounded-md">{completion}%</span>
                </div>
                <div className="mt-1.5 h-2 w-full bg-white/20 rounded-full overflow-hidden p-0.5">
                  <div 
                    className="bg-white h-full rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(255,255,255,0.8)]" 
                    style={{ width: `${completion}%` }}
                  />
                </div>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-2">
            <FontSizeSelector variant="compact" />
            <div className="p-2 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center overflow-hidden shrink-0">
              {user?.logoUrl ? (
                <img src={user.logoUrl} alt="Logo" className="h-6 w-6 rounded-full object-cover aspect-square shadow-sm" />
              ) : (
                <NicheIcon className="h-6 w-6 text-white" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area - Full-Height Flexible Container */}
      <div className="flex-1 flex flex-col justify-between px-4 pt-3 pb-6 overflow-y-auto">
        <div className="space-y-4 flex flex-col flex-1 justify-between">
          <div>
            {/* Banner Suave Reforma Tributária 2026/2027 */}
            <TaxReformNoticeBanner className="mb-4" />

            {/* Widget Saúde do Preço */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2 ml-1">
                <h2 className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Saúde do seu Preço</h2>
              </div>
              <ProfitThermometer 
                level={priceHealth.level} 
                percentage={priceHealth.percentage}
                label={priceHealth.label}
                description={priceHealth.description}
                onClick={onOpenMaterials}
              />
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button 
                onClick={() => setFilterStatus(filterStatus === 'sent' ? 'Todos' : 'sent')}
                className={`text-left transition-all duration-300 rounded-2xl p-4 shadow-sm border ${filterStatus === 'sent' ? 'bg-amber-50 border-amber-200' : 'bg-white border-zinc-100'}`}
              >
                <div className="flex items-center gap-2 text-amber-600 mb-1">
                  <Clock3 className="h-4 w-4" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">Abertos</span>
                </div>
                <p className="text-xl font-black text-zinc-900 truncate">
                  {formatCurrency(metrics.totalMoneyOnTable)}
                </p>
              </button>
              <button 
                onClick={() => setFilterStatus(filterStatus === 'approved' ? 'Todos' : 'approved')}
                className={`text-left transition-all duration-300 rounded-2xl p-4 shadow-sm border relative overflow-hidden ${filterStatus === 'approved' ? 'bg-brand/5 border-brand/20' : 'bg-white border-zinc-100'}`}
              >
                <div className="relative">
                  <div className="flex items-center gap-2 text-brand mb-1">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Lucro Livre</span>
                  </div>
                  <p className="text-xl font-black text-brand truncate">
                    {formatCurrency(metrics.totalNetProfit)}
                  </p>
                </div>
              </button>
            </div>

            <div className="mb-4">
              <div className="mb-2 ml-1">
                <h2 className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Últimos Orçamentos</h2>
              </div>

              {quotes.length === 0 ? (
                <div className="bg-white rounded-2xl p-6 text-center border border-zinc-100 shadow-sm text-[11px] text-zinc-400">
                  Nenhum orçamento cadastrado.
                </div>
              ) : (
                <div className="space-y-3">
                  {(quotes || []).slice().reverse().filter(q => {
                    if (filterStatus !== 'Todos') return q.status === filterStatus;
                    return true;
                  }).slice(0, 3).map((quote) => (
                    <div 
                      key={quote.id} 
                      onClick={() => setSelectedQuote(quote)}
                      className="bg-white rounded-2xl p-3 shadow-sm border border-zinc-100 cursor-pointer hover:bg-zinc-50 transition-colors duration-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-8 h-8 bg-brand/10 rounded-full flex items-center justify-center text-brand-dark font-black text-xs shrink-0">
                            {quote.clientName?.charAt(0) || 'C'}
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-bold text-zinc-900 text-xs truncate">{quote.clientName || 'Cliente'}</h3>
                            <p className="text-[10px] text-zinc-500 truncate">{quote.rawServiceName}</p>
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <div className="text-sm font-black text-brand">{formatCurrency(quote.finalPrice)}</div>
                        </div>
                      </div>
                      
                      {quote.status === 'sent' && (
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onUpdateQuoteStatus(quote.id, 'approved');
                            }}
                            className="flex-1 bg-brand text-white py-2 rounded-xl font-bold text-[10px] shadow-sm shadow-brand/20 active:scale-95 transition-transform"
                          >
                            FECHEI!
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              onUpdateQuoteStatus(quote.id, 'rejected');
                            }}
                            className="flex-1 bg-zinc-100 text-zinc-600 py-2 rounded-xl font-bold text-[10px] active:scale-95 transition-transform"
                          >
                            PERDI
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  <button
                    onClick={() => {
                      if (user?.plan === 'Grátis') {
                        setIsPaywallOpen(true);
                      } else {
                        onOpenHistory();
                      }
                    }}
                    className="w-full py-3 text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors flex items-center justify-center gap-1.5 rounded-2xl border border-emerald-100 shadow-xs min-h-11"
                  >
                    Ver histórico completo
                    {user?.plan === 'Grátis' ? <Zap className="h-3 w-3 ml-1 text-amber-500" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Core App Actions Container */}
          <div className="space-y-3">
            <button
              onClick={onOpenComparison}
              className="w-full bg-linear-to-br from-indigo-700 to-indigo-900 rounded-2xl p-3 shadow-xs text-left group overflow-hidden relative"
            >
              <div className="flex items-center gap-3 relative z-10">
                <div className="bg-white/10 p-2 rounded-xl border border-white/10 shrink-0">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xs font-bold text-white leading-tight">Radar de Mercado</h3>
                  <p className="text-indigo-200 text-[10px] opacity-75 truncate">Preços na sua região.</p>
                </div>
                <ChevronRight className="h-4 w-4 text-white/40 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>

            {/* Utility Row */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={onOpenMaterials}
                className="bg-white rounded-2xl p-3 shadow-sm border border-zinc-100 flex items-center gap-2 hover:bg-zinc-50 transition-colors group"
              >
                <div className="bg-amber-50 p-2 rounded-xl group-hover:scale-110 transition-transform shrink-0">
                  <Package className="h-4 w-4 text-amber-600" />
                </div>
                <span className="text-[9px] font-bold text-zinc-900 uppercase tracking-tight leading-tight">Centro de Custos</span>
              </button>
              
              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="bg-white rounded-2xl p-3 shadow-sm border border-zinc-100 flex items-center gap-2 hover:bg-emerald-50 transition-colors group"
              >
                <div className="bg-emerald-50 p-2 rounded-xl group-hover:scale-110 transition-transform shrink-0">
                  <MessageCircle className="h-4 w-4 text-emerald-600" />
                </div>
                <span className="text-[9px] font-bold text-zinc-900 uppercase tracking-tight leading-tight">Dúvidas Frequentes</span>
              </button>
            </div>
          </div>
        </div>
      </div>



      <HelpCenterModal 
        isOpen={isHelpModalOpen} 
        onClose={() => setIsHelpModalOpen(false)} 
        userNiche={user?.niche || user?.profession}
        userPlan={user?.plan}
      />

      <SetupProgressModal
        isOpen={isSetupModalOpen}
        onClose={handleCloseSetupModal}
        user={user}
        config={config}
        materials={materials}
        completion={completion}
        pendingTask={pendingTask}
        onEditProfile={onEditProfile}
        onOpenMaterials={onOpenMaterials}
      />

      {/* Quote Detail Modal */}
      {selectedQuote && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 animate-in fade-in">
          <div className="bg-white w-full max-w-md sm:max-w-lg rounded-t-4xl sm:rounded-4xl overflow-hidden shadow-2xl animate-in slide-in-from-bottom-8 sm:slide-in-from-bottom-0 sm:zoom-in-95 flex flex-col max-h-[95dvh] sm:max-h-[90vh]">
            <div className="p-6 border-b border-zinc-100 flex justify-between items-center bg-zinc-50">
              <div>
                <h2 className="text-lg font-bold text-zinc-900">Detalhes do Orçamento</h2>
                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-wider mt-1">
                  {formatDate(selectedQuote.createdAt)}
                </p>
              </div>
              <button 
                onClick={() => setSelectedQuote(null)}
                className="p-2 bg-white rounded-full hover:bg-zinc-100 transition-colors shadow-sm"
              >
                <XCircle className="h-5 w-5 text-zinc-400" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-brand/10 rounded-full flex items-center justify-center text-brand font-bold text-xl">
                    {selectedQuote.clientName?.charAt(0) || 'C'}
                  </div>
                  <div>
                    <h3 className="font-bold text-zinc-900 text-lg">{selectedQuote.clientName || 'Cliente'}</h3>
                    <p className="text-sm text-zinc-500">{selectedQuote.category}</p>
                  </div>
                </div>
                <div className={`flex items-center gap-1 px-3 py-1.5 rounded-xl border text-xs font-bold uppercase tracking-wider ${getStatusColor(selectedQuote.status)}`}>
                  {getStatusIcon(selectedQuote.status)}
                  {translateQuoteStatus(selectedQuote.status)}
                </div>
              </div>

              <div className="bg-zinc-50 rounded-2xl p-4 border border-zinc-100">
                <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest mb-1">Serviço</p>
                <p className="text-zinc-900 font-medium">{selectedQuote.rawServiceName}</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-zinc-100 rounded-2xl p-3 shadow-sm">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <Package className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Material</span>
                  </div>
                  <p className="font-mono text-sm font-medium">{formatCurrency(selectedQuote.materialCost)}</p>
                </div>
                <div className="bg-white border border-zinc-100 rounded-2xl p-3 shadow-sm">
                  <div className="flex items-center gap-2 text-zinc-500 mb-1">
                    <Clock3 className="h-4 w-4" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Tempo Estimado</span>
                  </div>
                  <p className="font-mono text-sm font-medium text-zinc-900">{formatMinutes(selectedQuote.timeSpentMinutes)}</p>
                </div>
              </div>

              <div className="bg-brand-dark text-white rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-brand/20 rounded-full blur-3xl -mr-16 -mt-16" />
                <div className="relative z-10 flex justify-between items-end">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-brand/50 text-[10px] font-bold uppercase tracking-widest">Valor Final</p>
                      <span className="bg-brand/20 text-brand text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                        +M. de Lucro: {Math.round(selectedQuote.appliedProfitMargin * 100)}%
                      </span>
                    </div>
                    <p className="text-3xl font-black">{formatCurrency(selectedQuote.finalPrice)}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-brand/30" />
                </div>
              </div>

              {/* Status History Section */}
              <div className="space-y-3">
                <h4 className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Histórico de Status</h4>
                <div className="space-y-4">
                  {(selectedQuote.statusHistory || []).map((entry, index) => (
                    <div key={index} className="flex gap-3 relative">
                      {index !== (selectedQuote.statusHistory?.length || 0) - 1 && (
                        <div className="absolute left-2.75 top-6 -bottom-4 w-0.5 bg-zinc-100" />
                      )}
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center z-10 ${
                        entry.status === 'approved' ? 'bg-emerald-100' : 
                        entry.status === 'rejected' ? 'bg-red-100' : 
                        entry.status === 'sent' ? 'bg-brand/10' : 'bg-zinc-100'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          entry.status === 'approved' ? 'bg-emerald-600' : 
                          entry.status === 'rejected' ? 'bg-red-600' : 
                          entry.status === 'sent' ? 'bg-brand' : 'bg-zinc-400'
                        }`} />
                      </div>
                      <div className="flex-1 pb-2">
                        <div className="flex justify-between items-start">
                          <p className="text-sm font-bold text-zinc-900">{translateQuoteStatus(entry.status)}</p>
                          <p className="text-[10px] text-zinc-400">{formatDate(entry.timestamp)}</p>
                        </div>
                        <p className="text-[10px] text-zinc-500 italic">
                          {entry.status === 'draft' ? 'Orçamento em elaboração' : 
                           entry.status === 'sent' ? 'Orçamento enviado ao cliente' : 
                           entry.status === 'approved' ? 'Venda confirmada pelo usuário' : 
                           'Orçamento marcado como perdido'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedQuote.status === 'sent' && (
                <div className="space-y-3 pt-2">
                  {/* --- ALERTA DE DRIFT DE CUSTO --- */}
                  {(() => {
                    const drifts = computeDriftAlerts(selectedQuote);
                    if (!drifts.length) return null;
                    return (
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
                        <div className="flex items-start gap-2">
                          <span className="text-amber-500 text-base leading-none mt-0.5">⚠</span>
                          <div>
                            <p className="font-bold text-amber-900 text-sm">Custos alterados desde a criação</p>
                            <p className="text-[11px] text-amber-700 mt-0.5">O preço de {drifts.length === 1 ? 'um material mudou' : `${drifts.length} materiais mudaram`} após este orçamento ser salvo. O valor do cliente não é afetado.</p>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {drifts.map((d, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white rounded-xl p-3 border border-amber-100">
                              <span className="text-xs font-bold text-zinc-700 truncate max-w-[60%]">{d.name}</span>
                              <div className="text-right">
                                <span className={`text-xs font-black ${ d.driftPct > 0 ? 'text-rose-600' : 'text-emerald-600' }`}>
                                  {d.driftPct > 0 ? '▲' : '▼'} {Math.abs(d.driftPct).toFixed(1)}%
                                </span>
                                <p className="text-[9px] text-zinc-400">
                                  {formatCurrency(d.snapshotPrice)} → {formatCurrency(d.currentPrice)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        onUpdateQuoteStatus(selectedQuote.id, 'approved');
                        setSelectedQuote({ 
                          ...selectedQuote, 
                          status: 'approved',
                          statusHistory: [
                            ...(selectedQuote.statusHistory || []),
                            { status: 'approved', timestamp: new Date().toISOString() }
                          ]
                        });
                        showToast('Venda Fechada!', 'success');
                      }}
                      className="bg-emerald-600 text-white py-3.5 rounded-xl font-bold uppercase tracking-wider text-sm shadow-md hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Fechar Venda
                    </button>
                    <button
                      onClick={() => {
                        onUpdateQuoteStatus(selectedQuote.id, 'rejected');
                        setSelectedQuote({ 
                          ...selectedQuote, 
                          status: 'rejected',
                          statusHistory: [
                            ...(selectedQuote.statusHistory || []),
                            { status: 'rejected', timestamp: new Date().toISOString() }
                          ]
                        });
                        showToast('Marcado como perdido.', 'info');
                      }}
                      className="bg-red-50 text-red-600 py-3.5 rounded-xl font-bold uppercase tracking-wider text-sm border border-red-100 hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      Recusado
                    </button>
                  </div>
                  
                  <button 
                    onClick={() => handleFollowUp(selectedQuote)}
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-3.5 rounded-xl flex items-center justify-center gap-2 font-bold uppercase tracking-widest text-sm transition-transform active:scale-[0.98] shadow-lg"
                  >
                    <Send className="h-4 w-4" /> Cobrar Retorno do Orçamento
                  </button>
                </div>
              )}

              <div className="pt-4 border-t border-zinc-100">
                <React.Suspense fallback={<div className="h-20 w-full animate-pulse bg-zinc-100 rounded-xl" />}>
                  <PDFExporter
                    user={user as any}
                    formData={{
                      ...selectedQuote,
                      clientName: selectedQuote.clientName,
                      serviceName: selectedQuote.rawServiceName,
                      materialCost: selectedQuote.materialCost,
                      timeHours: Math.floor(selectedQuote.timeSpentMinutes / 60),
                      timeMinutes: selectedQuote.timeSpentMinutes % 60,
                      products: selectedQuote.products,
                      services: selectedQuote.services
                    }}
                    finalPrice={selectedQuote.finalPrice}
                    quoteCount={quotes.length}
                    templateType={CATEGORIES.find(c => c.name === selectedQuote.category)?.templateType as any || 'STANDARD'}
                    saved={true}
                  />
                </React.Suspense>
              </div>

              {/* Ação Genérica: Todos os status podem ser reutilizados */}
              {onReuseQuote && (
                <div className="pt-4 mt-4 border-t border-zinc-100">
                  <button
                    onClick={() => {
                      onReuseQuote(selectedQuote);
                      setSelectedQuote(null);
                    }}
                    className="w-full bg-zinc-100 text-zinc-700 py-3.5 rounded-xl font-bold uppercase tracking-wider text-sm border border-zinc-200 hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2"
                  >
                    <RefreshCcw className="h-4 w-4" />
                    REUTILIZAR ORÇAMENTO
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-100 px-4 w-full max-w-sm"
          >
            <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl shadow-xl border ${
              toastMessage.type === 'success' ? 'bg-brand border-brand/50 text-white' :
              toastMessage.type === 'error' ? 'bg-rose-600 border-rose-500 text-white' :
              'bg-zinc-900 border-zinc-800 text-white'
            }`}>
              {toastMessage.type === 'success' && <CheckCircle2 className="h-5 w-5 shrink-0" />}
              {toastMessage.type === 'error' && <XCircle className="h-5 w-5 shrink-0" />}
              {toastMessage.type === 'info' && <Clock3 className="h-5 w-5 shrink-0" />}
              <p className="font-bold text-sm">{toastMessage.title}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <PaywallModal isOpen={isPaywallOpen} onClose={() => setIsPaywallOpen(false)} />
    </div>
  );
}
